import { ref } from 'vue';
import * as Sentry from '@sentry/vue';
import http from '@/services/http';

/**
 * Resolve location zone ids into the facts a schedule needs to say who wrote
 * it: the zone's name, its master (to tell a subscriber from any other zone),
 * its time zone (to read "now" on the zone's own clock), its scheduling
 * mode (shown beside the editor's Priority field) and how many zones subscribe
 * to it (the count on a custom-list card's zone line).
 *
 * Every id asked for in one render joins ONE search, like the current-track
 * resolver ({@link file:src/composables/api/useCurrentTrackSummaries.ts}):
 *
 *   POST /api/location_zone/search
 *   { perPage: ids.length, withTotal: false,
 *     filter: { ruleScopes: { id: 'root', scopeType: 'and',
 *       rules: [{ column: 'id', criterion: 'in', value: ids }] } } }
 *
 * Summaries are cached module-wide by id, so a zone resolved for one table
 * row is instant on every other row and in the inspector. A zone the search
 * does not return (deleted, or one this user may not see) is cached as a miss
 * on a shorter clock, and callers render it as "Zone #id". A failed request
 * caches nothing, so the next render asks again.
 *
 * The cache outlives components but not the session: logging out calls
 * {@link resetZoneSummaries}, because the SPA does not reload and the next user
 * must not be served zone names the server would refuse them.
 *
 * Asking is a side effect of reading: {@link zoneSummaryFor} queues the id and
 * returns whatever the cache holds, so a computed that reads it re-runs when
 * the answer lands.
 */

export interface ZoneSummary {
    id: number;
    displayString: string | null;
    /** null when the zone has no master. */
    masterLocationZoneId: number | null;
    /**
     * The name the zone's document carries for its time zone, such as
     * "US/Eastern", or null. It is the time zone's `name`, which is not always
     * the clock the players use: read a zone's clock through zoneWallClock.
     */
    timeZone: string | null;
    /** location_zones.time_zone_id. Optional so summaries built elsewhere need not carry it. */
    timeZoneId?: number | null;
    /**
     * scheduling_mode_id (1 Legacy, 2 Priority; null reads as Legacy). Optional
     * so summaries built elsewhere need not carry it; the lookup always sets it.
     */
    schedulingModeId?: number | null;
    /** How many zones name this one as their master. Optional for the same reason. */
    subscribersCount?: number;
    /**
     * The zone's location as the search document nests it, `{id, display_string}`,
     * so a zone line can link the location half of the name. Optional likewise.
     */
    location?: { id: number; display_string: string } | null;
    /**
     * is_tpg_location_zone, which says the zone's brand: TPG when true, Gray V
     * when false. null when the document did not carry it. Optional likewise.
     */
    isTpg?: boolean | null;
    /**
     * location_zone_status_id, which turns the zone's mark red when it is
     * Pending Cancelation. null when the document did not carry it. Optional likewise.
     */
    locationZoneStatusId?: number | null;
}

/**
 * - `undefined`: not answered yet (never asked, or the request is in flight).
 * - `null`: the search answered and the zone was not in it.
 * - a summary: resolved.
 */
export type ZoneLookup = ZoneSummary | null | undefined;

interface CacheEntry {
    summary: ZoneSummary | null;
    fetchedAt: number;
}

/** Zone names and masters change rarely; a long clock keeps table scrolling quiet. */
const RESOLVED_REFRESH_MS = 30 * 60_000;

/** A missing zone may just be one the index had not caught up on. */
const UNRESOLVED_RETRY_MS = 5 * 60_000;

const cache = ref(new Map<number, CacheEntry>());
const inFlight = new Set<number>();
let queue = new Set<number>();
let scheduled = false;
/** Bumped on reset, so an answer that lands after a logout is dropped. */
let generation = 0;

function isFresh(entry: CacheEntry, now: number): boolean {
    const ttl = entry.summary === null ? UNRESOLVED_RETRY_MS : RESOLVED_REFRESH_MS;
    return now - entry.fetchedAt < ttl;
}

function positiveId(value: unknown): number | null {
    const id = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
    return typeof id === 'number' && Number.isInteger(id) && id > 0 ? id : null;
}

function readTimeZone(zone: Record<string, unknown>): string | null {
    const timeZone = zone.time_zone;
    if (timeZone && typeof timeZone === 'object' && 'display_string' in timeZone) {
        const name = (timeZone as { display_string?: unknown }).display_string;
        return typeof name === 'string' && name.trim() !== '' ? name : null;
    }
    return null;
}

function readLocation(zone: Record<string, unknown>): ZoneSummary['location'] {
    const location = zone.location as { id?: unknown; display_string?: unknown } | null | undefined;
    const id = positiveId(location?.id);
    return id !== null && typeof location?.display_string === 'string' ? { id, display_string: location.display_string } : null;
}

/** One search per this many ids; the heaviest real list is scheduled by 431 zones. */
const BATCH_SIZE = 200;

async function fetchZones(ids: number[]): Promise<Map<number, ZoneSummary>> {
    const response = await http.post('/api/location_zone/search', {
        perPage: ids.length,
        withTotal: false,
        filter: {
            ruleScopes: {
                id: 'root',
                scopeType: 'and',
                rules: [{ id: 'zone-summaries', column: 'id', criterion: 'in', value: ids, field: null }],
            },
        },
    });
    const data = (response.data as { data?: Record<string, unknown>[] }).data ?? [];
    const byId = new Map<number, ZoneSummary>();
    for (const zone of data) {
        const id = positiveId(zone.id);
        if (id === null) continue;
        byId.set(id, {
            id,
            displayString:
                typeof zone.display_string === 'string' && zone.display_string.trim() !== '' ? zone.display_string : null,
            masterLocationZoneId: positiveId(zone.master_location_zone_id),
            timeZone: readTimeZone(zone),
            timeZoneId: positiveId(zone.time_zone_id),
            schedulingModeId: positiveId(zone.scheduling_mode_id),
            subscribersCount: typeof zone.subscribers_count === 'number' ? zone.subscribers_count : 0,
            location: readLocation(zone),
            isTpg: typeof zone.is_tpg_location_zone === 'boolean' ? zone.is_tpg_location_zone : null,
            locationZoneStatusId: positiveId(zone.location_zone_status_id),
        });
    }
    return byId;
}

async function flushBatch(ids: number[], batchGeneration: number): Promise<void> {
    try {
        const byId = await fetchZones(ids);
        // Asked for by the previous session: its answer is not this user's to see.
        if (batchGeneration !== generation) return;
        const now = Date.now();
        const next = new Map(cache.value);
        for (const id of ids) {
            next.set(id, { summary: byId.get(id) ?? null, fetchedAt: now });
        }
        cache.value = next;
    } catch (error) {
        // Nothing cached, so the next render re-asks. Reported so a broken
        // lookup is visible rather than silently leaving badges neutral.
        Sentry.captureException(error);
    } finally {
        if (batchGeneration === generation) {
            for (const id of ids) {
                inFlight.delete(id);
            }
        }
    }
}

async function flush(): Promise<void> {
    const ids = [...queue];
    const batchGeneration = generation;
    queue = new Set();
    scheduled = false;
    const batches: Promise<void>[] = [];
    for (let index = 0; index < ids.length; index += BATCH_SIZE) {
        batches.push(flushBatch(ids.slice(index, index + BATCH_SIZE), batchGeneration));
    }
    await Promise.all(batches);
}

function ask(id: number): void {
    const entry = cache.value.get(id);
    if ((entry && isFresh(entry, Date.now())) || inFlight.has(id)) {
        return;
    }
    inFlight.add(id);
    queue.add(id);
    if (!scheduled) {
        scheduled = true;
        // A microtask, so every id read in this render joins the same request.
        void Promise.resolve().then(flush);
    }
}

/**
 * The cached summary for one zone, queuing a lookup when it is missing or
 * stale. An expired entry keeps serving its old value while the refresh runs.
 */
export function zoneSummaryFor(zoneId: unknown): ZoneLookup {
    const id = positiveId(zoneId);
    if (id === null) {
        return null;
    }
    const entry = cache.value.get(id);
    ask(id);
    return entry ? entry.summary : undefined;
}

/** The cached summary without asking — for a caller that must not trigger a request. */
export function peekZoneSummary(zoneId: unknown): ZoneLookup {
    const id = positiveId(zoneId);
    if (id === null) {
        return null;
    }
    const entry = cache.value.get(id);
    return entry ? entry.summary : undefined;
}

/** Composable form, for components that prefer an injected API. */
export function useZoneSummaries() {
    return { zoneSummaryFor, peekZoneSummary };
}

/**
 * Forget every answer, and drop any lookup still in flight. Called on logout
 * (authStore.logout), and by tests, since the cache is module-level.
 */
export function resetZoneSummaries(): void {
    generation += 1;
    cache.value = new Map();
    inFlight.clear();
    queue = new Set();
    scheduled = false;
}

/** Test seam kept under its old name. */
export const __resetZoneSummaries = resetZoneSummaries;
